import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Key, ArrowLeft, Check, Shield } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import { useLanguage } from '../../contexts/LanguageContext';
import { ProfileCard, profileInputBase, profileLabelClass } from '../../components/profile/ProfileUi';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState({ currentPassword: false, newPassword: false, confirmPassword: false });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const toggle = (field) => setShow((p) => ({ ...p, [field]: !p[field] }));

  const handleChange = (field, value) => {
    setData((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validate = () => {
    const e = {};
    const errs = t('changePassword.errors');
    if (!data.currentPassword) e.currentPassword = errs.currentRequired;
    if (!data.newPassword) {
      e.newPassword = errs.newRequired;
    } else if (data.newPassword.length < 8) {
      e.newPassword = errs.newMinLength;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.newPassword)) {
      e.newPassword = errs.newStrength;
    }
    if (!data.confirmPassword) {
      e.confirmPassword = errs.confirmRequired;
    } else if (data.newPassword !== data.confirmPassword) {
      e.confirmPassword = errs.notMatch;
    }
    if (data.currentPassword && data.newPassword && data.currentPassword === data.newPassword) {
      e.newPassword = errs.sameAsCurrent;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await UserService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      if (result.success) {
        setSuccess(result.message || t('changePassword.successMsg'));
        toast.success(result.message || t('changePassword.successMsg'));
        setData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setErrors({});
        setTimeout(() => navigate('/profile'), 2000);
      } else {
        setError(result.message || t('common.error'));
        toast.error(result.message || t('common.error'));
      }
    } catch {
      setError(t('common.error'));
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const PasswordField = ({ fieldKey, label, placeholder }) => (
    <div>
      <label className={profileLabelClass}>{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          type={show[fieldKey] ? 'text' : 'password'}
          value={data[fieldKey]}
          onChange={(e) => handleChange(fieldKey, e.target.value)}
          placeholder={placeholder}
          className={`${profileInputBase} pl-9 pr-12 ${
            errors[fieldKey] ? 'border-red-500 focus:border-red-500' : ''
          }`}
        />
        <button
          type="button"
          onClick={() => toggle(fieldKey)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {show[fieldKey] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {errors[fieldKey] && <p className="mt-1 text-xs text-red-500">{errors[fieldKey]}</p>}
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-3xl mx-auto">
      {/* Header Card — tahsis style */}
      <div className="bg-white dark:bg-[#1C1C1E] rounded-[28px] p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 shadow-sm border border-gray-100 dark:border-white/10">
        <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-[#24299d] to-[#3840c4] flex items-center justify-center text-white shadow-lg shrink-0">
          <Key size={36} />
        </div>
        <div className="flex-1 text-center md:text-left min-w-0">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('changePassword.title')}</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5">{t('changePassword.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors shrink-0"
        >
          <ArrowLeft size={15} />
          {t('changePassword.backToProfile')}
        </button>
      </div>

      <ProfileCard title={t('changePassword.title')} icon={Lock} color="bg-blue-100 dark:bg-blue-900/30">
        <form onSubmit={handleSubmit} className="space-y-5">
          <PasswordField
            fieldKey="currentPassword"
            label={t('changePassword.currentPassword')}
            placeholder="••••••••"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PasswordField fieldKey="newPassword" label={t('changePassword.newPassword')} placeholder="••••••••" />
            <PasswordField fieldKey="confirmPassword" label={t('changePassword.confirmPassword')} placeholder="••••••••" />
          </div>

          <div className="flex gap-3 p-4 bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/10 rounded-2xl">
            <Shield size={16} className="text-[#24299d] dark:text-[#818cf8] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('changePassword.requirements')}</p>
              <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400 list-disc list-inside">
                <li>{t('changePassword.req1')}</li>
                <li>{t('changePassword.req2')}</li>
                <li>{t('changePassword.req3')}</li>
                <li>{t('changePassword.req4')}</li>
                <li>{t('changePassword.req5')}</li>
              </ul>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
              <Check size={16} /> {success}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1 border-t border-gray-100 dark:border-white/5">
            <button
              type="button"
              onClick={() => {
                setData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setErrors({});
                setError('');
                setSuccess('');
              }}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              {t('changePassword.resetButton')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#24299d] hover:bg-[#1b2080] text-white text-sm font-semibold shadow-sm transition-all active:scale-95 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('changePassword.changing')}
                </>
              ) : (
                <>
                  <Check size={16} />
                  {t('changePassword.changeButton')}
                </>
              )}
            </button>
          </div>
        </form>
      </ProfileCard>
    </div>
  );
};

export default ChangePassword;
